import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { ensureCCW } from '../../../geo/polygon';
import { triangulateRing } from '../solids';
import { panelPoint, panelUv, panelWall, type Panel, type PanelStyles } from '../panels';

export const panelCoordsOf = panelUv;

/** The style a wall face is drawn in and where its points lie on it. */
export interface WallFace {
  readonly style: VertexStyle;
  readonly uv: (p: Vec3) => Vec2;
}

/**
 * A wall face on `panel` within `outline` (panel coordinates). A patterned face (STYLE §15.1) runs u in
 * metres from its left end as seen from outside, which is the panel's u axis, and v at world height, so
 * courses and bands run level round the building and on up its gables, and carries its width and
 * height, where quoins find its ends. The face is laid in triangles, so it cannot leave that to the
 * builder, which would start every triangle afresh. An unpatterned face keeps the panel's coordinates
 * and its style's extent, which the painted shader's own wall patterns read.
 */
export function wallFace(style: VertexStyle, frame: Frame, panel: Panel, outline: Ring): WallFace {
  const local = panelCoordsOf(panel);
  if (!style.pattern) return { style, uv: local };
  const us = outline.map((p) => p[0]);
  const vs = outline.map((p) => p[1]);
  const u0 = Math.min(...us);
  const extent = [Math.max(...us) - u0, Math.max(...vs) - Math.min(...vs)] as const;
  return { style: { ...style, extent }, uv: (p) => [local(p)[0] - u0, p[1] + frame.origin[1]] };
}

/** A wall face with its openings drawn flush: panelWall, with a patterned face's own coordinates. */
export function plainWall(
  builder: MeshBuilder,
  styles: PanelStyles,
  frame: Frame,
  panel: Panel,
  outline: Ring,
  openings: readonly Ring[]
): void {
  if (!styles.wall.pattern) {
    panelWall(builder, styles, frame, panel, outline, openings);
    return;
  }
  const face = wallFace(styles.wall, frame, panel, outline);
  const holes = openings.map(ensureCCW);
  const at = (ring: readonly Vec2[]): Vec3[] => ring.map((p) => panelPoint(panel, p[0], p[1]));
  for (const tri of triangulateRing(ensureCCW(outline), holes)) builder.polygon(face.style, frame, at(tri), face.uv);
  for (const hole of holes) builder.polygon(styles.opening, frame, at(hole), panelCoordsOf(panel));
}
